import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import colors from "styles/colors";
import typography from "styles/typography";
import Text from "new_components/atoms/Text";
import { UPDATE_CULTURE_AUDIT_STATUS } from "constants/actions";

// Pulse animation for loading state
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

// Slide in animation
const slideIn = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Fade out animation
const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  min-width: 300px;
  max-width: 400px;
  background: ${colors.background.white};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-left: 4px solid ${(props) => props.borderColor};
  animation: ${slideIn} 0.3s ease-out;
  transition: all 0.3s ease;
  cursor: grab;
  user-select: none;
  touch-action: pan-x;
  
  ${(props) => props.isHiding && `
    animation: ${fadeOut} 0.3s ease-out forwards;
  `}
  
  ${(props) => props.swipeOffset && `
    transform: translateX(${props.swipeOffset}px);
    opacity: ${1 - props.swipeOffset / 200};
  `}
  
  &:active {
    cursor: grabbing;
  }
`;

const StatusIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  flex-shrink: 0;
  animation: ${(props) => (props.shouldPulse ? pulse : "none")} 1.5s ease-in-out infinite;
  transition: background-color 0.3s ease;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ButtonContainer = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
`;

const ActionButton = styled.button`
  background: ${colors.primary[500] || "#2D70E2"};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Raleway', sans-serif;
  
  &:hover {
    background: ${colors.primary[600] || "#2254AA"};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(45, 112, 226, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const MessageText = styled(Text)`
  line-height: 1.4;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  color: ${colors.text.secondary || "#6B7280"};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colors.neutral[100] || "#f3f4f6"};
    color: ${colors.text.dark || "#111827"};
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 4L4 12M4 4L12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Success checkmark icon
const SuccessIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.6667 5L7.50004 14.1667L3.33337 10"
      stroke={colors.secondary[600] || "#1D9A7D"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Error X icon
const ErrorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15 5L5 15M5 5L15 15"
      stroke={colors.auxiliary.red[500] || "#F15062"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Loading spinner icon
const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${colors.neutral[200] || "#f0f0f0"};
  border-top: 2px solid ${colors.primary[500] || "#2D70E2"};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

/**
 * Helper function to get status configuration
 */
const getStatusConfig = (status) => {
  const configs = {
    loading: {
      color: colors.primary[500] || "#2D70E2",
      borderColor: colors.primary[500] || "#2D70E2",
      shouldPulse: true,
      icon: <LoadingSpinner />,
    },
    success: {
      color: colors.secondary[600] || "#1D9A7D",
      borderColor: colors.secondary[600] || "#1D9A7D",
      shouldPulse: false,
      icon: <SuccessIcon />,
    },
    error: {
      color: colors.auxiliary.red[500] || "#F15062",
      borderColor: colors.auxiliary.red[500] || "#F15062",
      shouldPulse: false,
      icon: <ErrorIcon />,
    },
    idle: {
      color: colors.neutral[600] || "#6B7280",
      borderColor: colors.neutral[600] || "#6B7280",
      shouldPulse: false,
      icon: null,
    },
  };

  return configs[status] || configs.idle;
};

/**
 * CultureAuditLoadingToast Component
 * 
 * Displays a persistent toast notification showing the current status
 * of culture audit report operations. The toast appears when loading,
 * shows success/error states, and automatically hides idle states.
 * 
 * This component reads from Redux state: state.debrief_schedule.culture_audit_status
 */
const CultureAuditLoadingToast = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const cultureAuditStatus = useSelector(
    (state) => state.debrief_schedule?.culture_audit_status
  );
  const cultureAuditReports = useSelector(
    (state) => state.debrief_schedule?.culture_audit_reports
  );
  
  // Check if data is actually loaded
  const isDataLoaded = Array.isArray(cultureAuditReports?.response) && cultureAuditReports.response.length > 0;

  // Auto-hide success messages after 10 seconds (longer to allow button click)
  // Hooks must be called before any early returns
  const [isHiding, setIsHiding] = React.useState(false);
  
  // Swipe-to-dismiss state
  const [touchStart, setTouchStart] = React.useState(null);
  const [touchEnd, setTouchEnd] = React.useState(null);
  const [swipeOffset, setSwipeOffset] = React.useState(0);
  
  const handleDismiss = React.useCallback(() => {
    setIsHiding(true);
    // Clear status after fade out animation
    setTimeout(() => {
      dispatch({
        type: UPDATE_CULTURE_AUDIT_STATUS,
        payload: null,
      });
      setIsHiding(false);
    }, 300);
  }, [dispatch]);
  
  // Swipe handlers for mobile
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    if (!touchStart) return;
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    const distance = currentTouch - touchStart;
    // Only allow swiping right (dismissing to the right) - positive distance
    if (distance > 0) {
      setSwipeOffset(Math.min(distance, 200)); // Cap at 200px
    }
  };
  
  const onTouchEnd = () => {
    if (!touchStart || touchEnd === null) {
      setTouchStart(null);
      setTouchEnd(null);
      setSwipeOffset(0);
      return;
    }
    
    const distance = touchEnd - touchStart;
    const isRightSwipe = distance > minSwipeDistance;
    
    if (isRightSwipe) {
      handleDismiss();
    } else {
      // Reset position if swipe wasn't far enough
      setSwipeOffset(0);
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  React.useEffect(() => {
    // Reset swipe offset when status changes
    setSwipeOffset(0);
  }, [cultureAuditStatus?.status]);
  
  // Auto-dismiss when user navigates to analytics page while toast is showing AND data is loaded
  React.useEffect(() => {
    if (!cultureAuditStatus || cultureAuditStatus.status === "idle" || isHiding) {
      return;
    }
    
    const isOnAnalyticsPage = 
      location.pathname === "/leader/audit/new-analytics" ||
      location.pathname === "/app/audit/new-analytics";
    
    if (isOnAnalyticsPage && isDataLoaded) {
      // User navigated to the analytics page AND data is loaded - dismiss the toast
      handleDismiss();
    }
  }, [location.pathname, cultureAuditStatus, isHiding, handleDismiss, isDataLoaded]);
  
  React.useEffect(() => {
    // Reset hiding state when status changes
    setIsHiding(false);
    
    if (cultureAuditStatus?.status === "success") {
      // Check if user is already on the analytics page AND data is loaded - auto-dismiss immediately
      const isOnAnalyticsPage = 
        location.pathname === "/leader/audit/new-analytics" ||
        location.pathname === "/app/audit/new-analytics";
      
      if (isOnAnalyticsPage && isDataLoaded) {
        // Auto-dismiss immediately if already on the target page AND data is loaded
        setIsHiding(true);
        setTimeout(() => {
          dispatch({
            type: UPDATE_CULTURE_AUDIT_STATUS,
            payload: null,
          });
          setIsHiding(false);
        }, 300);
        return;
      }
      
      // Otherwise, auto-hide after 10 seconds
      const timer = setTimeout(() => {
        setIsHiding(true);
        // Remove from DOM after fade out animation
        setTimeout(() => {
          setIsHiding(false);
        }, 300);
      }, 10000); // Increased to 10 seconds to allow time for button interaction
      
      return () => clearTimeout(timer);
    }
  }, [cultureAuditStatus?.status, cultureAuditStatus?.timestamp, location.pathname, dispatch, isDataLoaded]);

  // Don't show toast for idle state or if hiding
  if (!cultureAuditStatus || cultureAuditStatus.status === "idle" || isHiding) {
    return null;
  }

  const statusConfig = getStatusConfig(cultureAuditStatus.status);
  const message = cultureAuditStatus.message || "Processing...";
  const isSuccess = cultureAuditStatus.status === "success";
  const isError = cultureAuditStatus.status === "error";

  // Determine the correct culture audit path based on current route
  const getCultureAuditPath = () => {
    if (location.pathname.startsWith("/leader")) {
      return "/leader/audit/new-analytics";
    } else if (location.pathname.startsWith("/app")) {
      return "/app/audit/new-analytics";
    }
    // Default fallback
    return "/leader/audit/new-analytics";
  };

  const handleViewReports = () => {
    navigate(getCultureAuditPath());
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <ToastContainer
      borderColor={statusConfig.borderColor}
      isHiding={isHiding}
      swipeOffset={swipeOffset}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <StatusIndicator
        color={statusConfig.color}
        shouldPulse={statusConfig.shouldPulse}
      />
      
      {statusConfig.icon && (
        <IconContainer>
          {statusConfig.icon}
        </IconContainer>
      )}
      
      <ContentContainer>
        <MessageText
          typographyStyle={typography.body.regular}
          color={colors.text.dark}
        >
          {message}
        </MessageText>
        {isError && (
          <ButtonContainer>
            <ActionButton onClick={handleRefresh}>
              Refresh
            </ActionButton>
          </ButtonContainer>
        )}
        {/* {isSuccess && (
          <ButtonContainer>
            <ActionButton onClick={handleViewReports}>
              Select Data Source
            </ActionButton>
          </ButtonContainer>
        )} */}
      </ContentContainer>
      
      <CloseButton
        onClick={handleDismiss}
        aria-label="Dismiss notification"
        type="button"
      >
        <CloseIcon />
      </CloseButton>
    </ToastContainer>
  );
};

export default CultureAuditLoadingToast;

